import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  confirmTossPayment,
  CONFIRM_REJECTED_REASONS,
  getPublicSession,
  PublicApiError,
} from '../api/public-sessions';

/**
 * Toss post-payment return page (success path).
 *
 * Toss redirects here after the user completes card authorization.
 * The page reads paymentKey/orderId/amount from the URL (appended
 * by Toss) plus our own sessionId/token (seeded into the successUrl
 * by lib/toss.ts), calls the server's confirm endpoint, and then
 * redirects to the client product's successUrl.
 *
 * See .claude/specs/008-web-toss-sdk-integration/subSPEC-04-toss-return-pages.md.
 */

type ConfirmState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'missing_params'; readonly reason: string }
  | { readonly phase: 'confirming' }
  | { readonly phase: 'succeeded'; readonly successUrl: string | null }
  | {
      readonly phase: 'failed';
      readonly message: string;
      readonly requestId?: string;
    };

export function TossSuccessPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<ConfirmState>({ phase: 'loading' });

  // React 18 Strict Mode mount guard — prevents confirmTossPayment
  // from being called twice, which would trip the server's
  // session_invalid_state defense on the second call.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    void runConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runConfirm(): Promise<void> {
    const token = searchParams.get('token') ?? '';
    const sessionId = searchParams.get('sessionId') ?? '';
    const paymentKey = searchParams.get('paymentKey') ?? '';
    const orderId = searchParams.get('orderId') ?? '';
    const amountRaw = searchParams.get('amount') ?? '';

    if (!token || !sessionId || !paymentKey || !orderId || !amountRaw) {
      setState({
        phase: 'missing_params',
        reason: 'Missing required Toss redirect parameters',
      });
      return;
    }

    const amount = Number.parseInt(amountRaw, 10);
    if (!Number.isFinite(amount) || amount < 1) {
      setState({
        phase: 'missing_params',
        reason: 'Invalid amount parameter',
      });
      return;
    }

    setState({ phase: 'confirming' });

    try {
      const result = await confirmTossPayment(sessionId, token, {
        paymentKey,
        orderId,
        amount,
      });

      setState({ phase: 'succeeded', successUrl: result.successUrl });

      // Let React commit the "Payment complete" frame before redirect
      setTimeout(() => {
        try {
          if (result.successUrl) {
            window.location.href = result.successUrl;
          } else {
            navigate('/success', { replace: true });
          }
        } catch {
          navigate('/success', { replace: true });
        }
      }, 0);
    } catch (err: unknown) {
      await handleConfirmError(err, { sessionId, token });
    }
  }

  async function handleConfirmError(
    err: unknown,
    ctx: { sessionId: string; token: string },
  ): Promise<void> {
    if (err instanceof PublicApiError) {
      const code = err.payload.code;
      const requestId = err.payload.requestId;

      // Double-confirm via refresh mitigation: if the session is
      // already completed from a prior successful confirm, redirect
      // to its successUrl instead of showing an error.
      if (code === 'session_invalid_state') {
        try {
          const session = await getPublicSession(ctx.sessionId, ctx.token);
          if (session.status === 'completed') {
            if (session.successUrl) {
              window.location.href = session.successUrl;
            } else {
              navigate('/success', { replace: true });
            }
            return;
          }
        } catch {
          // fall through to generic error
        }
      }

      const reason =
        CONFIRM_REJECTED_REASONS[
          code as keyof typeof CONFIRM_REJECTED_REASONS
        ];

      if (reason) {
        navigate(
          `/cancel?reason=${encodeURIComponent(reason)}&requestId=${encodeURIComponent(requestId)}`,
          { replace: true },
        );
      } else {
        navigate(
          `/error?requestId=${encodeURIComponent(requestId)}&code=${encodeURIComponent(code)}`,
          { replace: true },
        );
      }
      return;
    }

    // Plain network error — let the user retry
    setState({
      phase: 'failed',
      message:
        'Network error while confirming payment. Please check your connection and try again.',
    });
  }

  function handleRetry(): void {
    hasRunRef.current = false;
    setState({ phase: 'loading' });
    void runConfirm();
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (state.phase === 'missing_params') {
    return (
      <main className="co-container" data-phase={state.phase}>
        <div className="co-card">
          <h1>Something went wrong</h1>
          <p className="co-muted">{state.reason}</p>
          <button
            className="co-button co-button-link"
            onClick={() => navigate('/error', { replace: true })}
          >
            Go to support
          </button>
        </div>
      </main>
    );
  }

  if (state.phase === 'failed') {
    return (
      <main className="co-container" data-phase={state.phase}>
        <div className="co-card">
          <h1>Payment Confirmation Failed</h1>
          <p className="co-muted">{state.message}</p>
          <button
            className="co-button co-button-primary"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      </main>
    );
  }

  if (state.phase === 'succeeded') {
    return (
      <main className="co-container" data-phase={state.phase}>
        <div className="co-card">
          <p className="co-muted">Payment complete. Redirecting…</p>
        </div>
      </main>
    );
  }

  // loading | confirming
  return (
    <main className="co-container" data-phase={state.phase}>
      <div className="co-card">
        <p className="co-muted">◐ Processing</p>
        <p className="co-muted">Confirming your payment with Toss…</p>
        <p className="co-muted co-small">
          This usually takes a few seconds. Please do not close this window.
        </p>
      </div>
    </main>
  );
}
